import React, { useState } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useTenant } from '../contexts/TenantContext';
import { useAnalytics, useAnalyticsSummary, useAnalyticsTimeSeries } from '../hooks/useAnalytics';
import { Users, CreditCard, Megaphone, TrendingUp, History, ClipboardList, Calendar, Filter, Share2 } from 'lucide-react';
import { useAuditLogs } from '../hooks/useAudit';
import { safeFormat, cn } from '../lib/utils';

const COLORS = ['#0F1F3D', '#1A56A0', '#4285F4', '#90CAF9', '#BBDEFB'];

export const AnalyticsPage = () => {
  const { node, getTerm } = useTenant();
  const [range, setRange] = useState('30d');
  const [isRollingUp, setIsRollingUp] = useState(false);
  
  const { data: analytics, isLoading: isOldAnalyticsLoading } = useAnalytics(node?.id || '');
  const { data: summary, isLoading: isSummaryLoading } = useAnalyticsSummary(node?.id || '', isRollingUp);
  const { data: timeseries, isLoading: isTimeseriesLoading } = useAnalyticsTimeSeries(node?.id || '', range, 'members,revenue,engagement', isRollingUp);
  const { data: auditLogs = [] } = useAuditLogs(node?.id || '', 10);

  const isLoading = isOldAnalyticsLoading || isSummaryLoading || isTimeseriesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-none animate-spin" />
      </div>
    );
  }

  // Fallback if data is missing
  if (!summary || !timeseries) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <TrendingUp size={48} className="mb-4 opacity-20" />
        <p>No analytics data available for this node.</p>
      </div>
    );
  }

  const safeSummary = {
    totalMembers: summary.totalMembers || 0,
    memberGrowthPercent: summary.memberGrowthPercent || 0,
    totalRevenue: summary.totalRevenue || 0,
    revenueGrowthPercent: summary.revenueGrowthPercent || 0,
    engagementRate: summary.engagementRate || 0,
    upcomingEventsCount: summary.upcomingEventsCount || 0
  };

  const ranges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' },
    { id: '1y', label: '1 Year' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-sm text-gray-500">Insights and trends for {node?.name}</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Rollup Toggle */}
          <div className="flex items-center bg-white p-1 rounded-none border-2 border-black dark:border-white dark:bg-slate-800">
            <button 
              onClick={() => setIsRollingUp(false)}
              className={cn(
                "px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-none transition-all",
                !isRollingUp ? "bg-white text-brand-primary shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
              )}
            >
              This Org
            </button>
            <button 
              onClick={() => setIsRollingUp(true)}
              className={cn(
                "px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-none transition-all flex items-center gap-1.5",
                isRollingUp ? "bg-white text-brand-primary shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900" : "text-gray-500 hover:text-gray-700"
              )}
            >
              <Share2 size={12} />
              Lineage Rollup
            </button>
          </div>

          {/* Date Selector */}
          <div className="flex items-center bg-white border-2 border-black dark:border-white rounded-none overflow-hidden shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
            {ranges.map((r) => (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border-r last:border-r-0 border-black dark:border-white transition-colors",
                  range === r.id ? "bg-brand-primary text-white" : "text-gray-500 hover:bg-white dark:bg-slate-950"
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="stat">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-none">
              <Users size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Total {getTerm('member')}s</p>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{safeSummary.totalMembers.toLocaleString()}</h3>
              <p className={cn(
                "text-[10px] font-bold flex items-center gap-0.5",
                safeSummary.memberGrowthPercent >= 0 ? "text-green-600" : "text-red-600"
              )}>
                <TrendingUp size={10} className={cn(safeSummary.memberGrowthPercent < 0 && "rotate-180")} />
                {Math.abs(safeSummary.memberGrowthPercent)}% this period
              </p>
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 text-green-600 rounded-none">
              <CreditCard size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Gross Revenue</p>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">${safeSummary.totalRevenue.toLocaleString()}</h3>
              <p className={cn(
                "text-[10px] font-bold flex items-center gap-0.5",
                safeSummary.revenueGrowthPercent >= 0 ? "text-green-600" : "text-red-600"
              )}>
                <TrendingUp size={10} className={cn(safeSummary.revenueGrowthPercent < 0 && "rotate-180")} />
                {Math.abs(safeSummary.revenueGrowthPercent)}% this period
              </p>
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-none">
              <Megaphone size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Engagement Rate</p>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{safeSummary.engagementRate}%</h3>
              <p className="text-[10px] text-gray-400 font-bold">Avg. reads per post</p>
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 text-amber-600 rounded-none">
              <Calendar size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Upcoming Events</p>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{safeSummary.upcomingEventsCount}</h3>
              <p className="text-[10px] text-blue-600 font-bold">Next 7 days</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Member Growth Chart - Real TimeSeries */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Member Retention & Growth</h3>
            <Badge variant="blue" className="text-[9px] font-mono">TimeSeries</Badge>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={timeseries.members}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                  tickFormatter={(val) => safeFormat(val, 'MMM d')}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                />
                <Tooltip 
                  labelFormatter={(val) => safeFormat(val as any, 'MMMM d, yyyy')}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Line 
                   type="monotone" 
                   dataKey="value" 
                   stroke="#1A56A0" 
                   strokeWidth={3} 
                   dot={{ r: 4, fill: '#1A56A0', strokeWidth: 0 }} 
                   activeDot={{ r: 6 }}
                   name={`${getTerm('member')}s`}
                 />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Revenue Chart - Real TimeSeries */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">Revenue Accumulation</h3>
            <Badge variant="blue" className="text-[9px] font-mono">Financials</Badge>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <AreaChart data={timeseries.revenue}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0F1F3D" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#0F1F3D" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                  tickFormatter={(val) => safeFormat(val, 'MMM d')}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                />
                <Tooltip 
                  labelFormatter={(val) => safeFormat(val as any, 'MMMM d, yyyy')}
                  formatter={(value: any) => [`$${(value || 0).toLocaleString()}`, 'Revenue']}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Area 
                   type="monotone" 
                   dataKey="value" 
                   stroke="#0F1F3D" 
                   fill="url(#colorRevenue)"
                   strokeWidth={3} 
                   dot={false}
                   name="Revenue"
                 />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Legacy / Snapshot Analytics (Still useful for some views) */}
        {analytics && (
          <>
            {/* Engagement Snapshot */}
            <Card className="p-6">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-wider">Pulse Engagement Stack</h3>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={analytics.announcementEngagement} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis 
                      type="number"
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                    />
                    <YAxis 
                      dataKey="name"
                      type="category"
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fontSize: 10, fill: '#9CA3AF' }} 
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                    <Bar dataKey="reads" fill="#1A56A0" radius={[0, 4, 4, 0]} barSize={16} name="Reads" />
                    <Bar dataKey="ack" fill="#4285F4" radius={[0, 4, 4, 0]} barSize={16} name="Acknowledgments" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Role Distribution Snapshot */}
            <Card className="p-6">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-6 uppercase tracking-wider">Role Distribution</h3>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie
                      data={analytics.memberRoles}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {analytics.memberRoles.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: '10px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Audit Trail */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-black dark:border-white flex items-center justify-between bg-white/30 dark:bg-slate-950">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-brand-primary/10 text-brand-primary rounded-none">
              <ClipboardList size={18} />
            </div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">System Audit Trail</h3>
          </div>
          <p className="text-[10px] font-mono text-gray-400">Security & Integrity Log</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white/50 dark:bg-slate-950">
                <th className="px-6 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono border-b border-black dark:border-white">User</th>
                <th className="px-6 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono border-b border-black dark:border-white">Action</th>
                <th className="px-6 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono border-b border-black dark:border-white">Resource</th>
                <th className="px-6 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono border-b border-black dark:border-white text-right">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {auditLogs.length > 0 ? (
                auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-white/80 transition-colors dark:bg-slate-950">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-500">
                          {(log.userName || '??').substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-700">{log.userName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="gray" className="font-mono text-[9px] uppercase bg-white border-black dark:border-white dark:bg-slate-800">
                        {log.action}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono italic">{log.resourceType}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-xs text-gray-400 font-mono">{safeFormat(log.createdAt, 'MMM d, h:mm a')}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400 italic">
                    No recent audit logs recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
